// Boletim diário da empresa: envio automático, destinatários, fontes, instrução da IA e prévia.
// Salvo em inquilinos/{tenant}.boletim (o backend lê a cada envio); o agendamento fica em
// agendamentos/boletim_{tenant} (tarefa "Boletim"). Sem configuração salva, vale o padrão do servidor.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiEye, FiPlus, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../config/firebaseConfig';
import Carregando from './Carregando';
import './BoletimEditor.css';

const apiUrl = process.env.REACT_APP_API_URL;
const MODELOS = ['gpt-4o-mini', 'gpt-4o', 'gemini-2.0-flash'];
const DIAS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

export default function BoletimEditor() {
  const tenantId = localStorage.getItem('tenantId');
  const idAgendamento = `boletim_${tenantId}`;
  const [config, setConfig] = useState(null);
  const [agenda, setAgenda] = useState(null);
  const [destinatariosDefinidos, setDestinatariosDefinidos] = useState(false);
  const [alterado, setAlterado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [previa, setPrevia] = useState(null);
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    (async () => {
      const padrao = await fetch('/boletim.config.json').then((r) => r.json()).catch(() => ({}));
      const inquilino = await getDoc(doc(firestore, 'inquilinos', tenantId));
      const salvo = (inquilino.exists() && inquilino.data().boletim) || {};
      const agendamento = await getDoc(doc(firestore, 'agendamentos', idAgendamento));
      setDestinatariosDefinidos(Array.isArray(salvo.destinatarios));
      setConfig({
        rssFeeds: salvo.rssFeeds?.length ? salvo.rssFeeds : padrao.rssFeeds || [],
        prompt: { modelo: 'gpt-4o-mini', texto: '', ...(padrao.prompt || {}), ...(salvo.prompt || {}) },
        limites: { itensPorFeed: 3, maxResumoChars: 250, ...(padrao.limites || {}), ...(salvo.limites || {}) },
        destinatarios: (salvo.destinatarios || []).join('\n'),
      });
      const a = agendamento.exists() ? agendamento.data() : {};
      setAgenda({ status: a.status === true, horario: a.horario || '08:00', frequencia: a.frequencia || 'Segunda-a-sexta', dia: a.dia || 'Segunda' });
    })().catch((e) => {
      console.error('Boletim:', e);
      toast.error('Não foi possível carregar o boletim.');
    });
  }, [tenantId, idAgendamento]);

  const mudar = (fn) => { fn(); setAlterado(true); };
  const mudarFeed = (i, campo, valor) =>
    mudar(() => setConfig((c) => ({ ...c, rssFeeds: c.rssFeeds.map((f, j) => (j === i ? { ...f, [campo]: valor } : f)) })));

  const numeros = (config?.destinatarios || '').split(/[\n,;]+/).map((n) => n.replace(/\D/g, '')).filter(Boolean);

  const salvar = async () => {
    setSalvando(true);
    try {
      await setDoc(doc(firestore, 'inquilinos', tenantId), {
        boletim: {
          rssFeeds: config.rssFeeds.filter((f) => f.url && f.url.trim()).map((f) => ({ ...f, url: f.url.trim() })),
          prompt: config.prompt,
          limites: { itensPorFeed: Number(config.limites.itensPorFeed) || 3, maxResumoChars: Number(config.limites.maxResumoChars) || 250 },
          destinatarios: numeros,
        },
      }, { merge: true });
      await setDoc(doc(firestore, 'agendamentos', idAgendamento), {
        nome: 'Boletim', tenantId, status: agenda.status, horario: agenda.horario,
        frequencia: agenda.frequencia, dia: agenda.frequencia === 'Semanal' ? agenda.dia : '',
      }, { merge: true });
      setDestinatariosDefinidos(true);
      setAlterado(false);
      return true;
    } catch (e) {
      console.error('Erro ao salvar o boletim:', e);
      toast.error('Não foi possível salvar o boletim.');
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const gerarPrevia = async () => {
    if (alterado && !(await salvar())) return;
    setGerando(true);
    try {
      const r = await axios.post(`${apiUrl}/boletim/previa`, { sanitizedClientId: tenantId });
      setPrevia(r.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível gerar a prévia.');
    } finally {
      setGerando(false);
    }
  };

  if (!config || !agenda) return <Carregando texto="Carregando o boletim..." />;

  return (
    <div className="bl-pagina">
      <header>
        <h2>Boletim diário</h2>
        <p className="bl-suave">Notícias das fontes abaixo, resumidas pela IA e enviadas pelo WhatsApp do bot.</p>
      </header>

      <section className="qz-card bl-secao">
        <h3>Envio automático</h3>
        <label className="bl-opcao">
          <input type="checkbox" checked={agenda.status} onChange={(e) => mudar(() => setAgenda((a) => ({ ...a, status: e.target.checked })))} />
          <span>Enviar o boletim automaticamente</span>
        </label>
        <div className="bl-linha">
          <label className="bl-campo">
            <span>Horário</span>
            <input type="time" value={agenda.horario} onChange={(e) => mudar(() => setAgenda((a) => ({ ...a, horario: e.target.value })))} />
          </label>
          <label className="bl-campo">
            <span>Frequência</span>
            <select value={agenda.frequencia} onChange={(e) => mudar(() => setAgenda((a) => ({ ...a, frequencia: e.target.value })))}>
              <option value="Diariamente">Todos os dias</option>
              <option value="Segunda-a-sexta">Segunda a sexta</option>
              <option value="Semanal">Uma vez por semana</option>
            </select>
          </label>
          {agenda.frequencia === 'Semanal' && (
            <label className="bl-campo">
              <span>Dia</span>
              <select value={agenda.dia} onChange={(e) => mudar(() => setAgenda((a) => ({ ...a, dia: e.target.value })))}>
                {DIAS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}
        </div>
      </section>

      <section className="qz-card bl-secao">
        <h3>Destinatários</h3>
        <p className="bl-suave">Um número por linha, com DDI e DDD (ex.: 5511999998888). O bot precisa estar conectado.</p>
        <textarea
          rows={4}
          aria-label="Destinatários"
          value={config.destinatarios}
          onChange={(e) => mudar(() => setConfig((c) => ({ ...c, destinatarios: e.target.value })))}
          placeholder="5511999998888"
        />
        <p className="bl-suave">
          {destinatariosDefinidos || alterado
            ? `${numeros.length} ${numeros.length === 1 ? 'destinatário' : 'destinatários'}${numeros.length ? '' : ': ninguém recebe'}.`
            : 'Ainda não definido aqui: o servidor usa a lista padrão dele até você salvar.'}
        </p>
      </section>

      <section className="qz-card bl-secao">
        <h3>Fontes de notícias (RSS)</h3>
        <ul className="bl-feeds">
          {config.rssFeeds.map((f, i) => (
            <li key={i} className="bl-feed">
              <input type="checkbox" aria-label="Fonte ativa" checked={f.ativo !== false} onChange={(e) => mudarFeed(i, 'ativo', e.target.checked)} />
              <input className="bl-feed__url" aria-label="Endereço do RSS" value={f.url} onChange={(e) => mudarFeed(i, 'url', e.target.value)} placeholder="https://site.com/feed" />
              <input className="bl-feed__cat" aria-label="Categoria" value={f.categoria || ''} onChange={(e) => mudarFeed(i, 'categoria', e.target.value)} placeholder="categoria" />
              <button type="button" className="btn-ghost bl-icone" aria-label="Remover fonte" title="Remover fonte"
                onClick={() => mudar(() => setConfig((c) => ({ ...c, rssFeeds: c.rssFeeds.filter((_, j) => j !== i) })))}>
                <FiTrash2 aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="btn-secondary bl-botao"
          onClick={() => mudar(() => setConfig((c) => ({ ...c, rssFeeds: [...c.rssFeeds, { url: '', categoria: '', ativo: true }] })))}>
          <FiPlus aria-hidden="true" /> Adicionar fonte
        </button>
      </section>

      <section className="qz-card bl-secao">
        <h3>Instrução para a IA</h3>
        <p className="bl-suave">Como a IA deve escolher e escrever as notícias (tom, formato, tamanho).</p>
        <textarea
          rows={10}
          aria-label="Instrução para a IA"
          value={config.prompt.texto}
          onChange={(e) => mudar(() => setConfig((c) => ({ ...c, prompt: { ...c.prompt, texto: e.target.value } })))}
        />
        <div className="bl-linha">
          <label className="bl-campo">
            <span>Modelo</span>
            <select value={config.prompt.modelo} onChange={(e) => mudar(() => setConfig((c) => ({ ...c, prompt: { ...c.prompt, modelo: e.target.value } })))}>
              {[...new Set([config.prompt.modelo, ...MODELOS])].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="bl-campo">
            <span>Notícias por fonte</span>
            <input type="number" min={1} max={10} value={config.limites.itensPorFeed}
              onChange={(e) => mudar(() => setConfig((c) => ({ ...c, limites: { ...c.limites, itensPorFeed: e.target.value } })))} />
          </label>
          <label className="bl-campo">
            <span>Máx. caracteres do resumo</span>
            <input type="number" min={50} max={2000} step={50} value={config.limites.maxResumoChars}
              onChange={(e) => mudar(() => setConfig((c) => ({ ...c, limites: { ...c.limites, maxResumoChars: e.target.value } })))} />
          </label>
        </div>
        {config.prompt.modelo.startsWith('gemini') && <p className="bl-suave">Modelos gemini-* exigem GEMINI_API_KEY no servidor.</p>}
      </section>

      <div className="bl-acoes">
        <span className="bl-suave">{alterado ? 'Há alterações não salvas.' : 'Tudo salvo.'}</span>
        <button type="button" className="btn-secondary bl-botao" onClick={gerarPrevia} disabled={gerando}>
          <FiEye aria-hidden="true" /> {gerando ? 'Gerando (leva uns 30s)...' : 'Gerar prévia'}
        </button>
        <button type="button" className="btn-primary bl-botao" disabled={!alterado || salvando}
          onClick={async () => { if (await salvar()) toast.success('Boletim salvo.'); }}>
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {previa && (
        <section className="qz-card bl-secao">
          <h3>Prévia (não foi enviada)</h3>
          <p className="bl-suave">Seria enviada para {previa.destinatarios} {previa.destinatarios === 1 ? 'destinatário' : 'destinatários'}.</p>
          <pre className="bl-previa">{previa.texto}</pre>
        </section>
      )}
    </div>
  );
}
